import './style.scss';

import { type KeyboardEvent, useMemo, useRef, useState } from 'react';

import { getUserData, ISOBSERVER } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { useCancellationStatus } from '../../../hooks/useCancellationStatus';
import { useObserverData } from '../../../hooks/useObserverData';
import { puCancellationEnabled } from '../../../lib/cancellation-api';
import { openCancellationPanel } from '../pu-cancellation/home';
import { evidenceAllowed } from '../pu-cancellation/shared';
import MyReports from './my-reports';
import Nearby from './nearby';
import NewReport from './new-report';
import { useIncidentLive } from './use-incident-live';
import {
	contextFromAssignment,
	contextFromObserverPosting,
	contextFromSitrepLocation,
	type HubSegment,
	type IReportsHubData,
} from './utils';

const SEGMENTS: { key: HubSegment; label: string; short: string }[] = [
	{ key: 'new', label: 'New Report', short: 'New' },
	{ key: 'mine', label: 'My Reports', short: 'Mine' },
	{ key: 'nearby', label: 'Nearby', short: 'Nearby' },
];

/**
 * The unified Reports hub right-section panel: a guided situation-report
 * submit flow, the agent's own reports with live status, and incidents
 * reported around the agent's location. App issues (the old report-issues
 * form) stay a separate feature, linked from the footer.
 */
const ReportsHub = () => {
	const { rsProps, states } = useGlobalContext();
	const data = (rsProps?.data ?? {}) as IReportsHubData;

	const [segment, setSegment] = useState<HubSegment>(data.initialSegment ?? 'new');
	const [mineCount, setMineCount] = useState<number | null>(null);
	const [nearbyCount, setNearbyCount] = useState<number | null>(null);
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

	// Auto-tag context: the entry point's full ancestry when provided
	// (View-Result), otherwise derived from the agent's own assignment.
	const isObserver = ISOBSERVER();
	const observer = useObserverData(isObserver);

	const ctx = useMemo(() => {
		if (data.sitrepLocation) return contextFromSitrepLocation(data.sitrepLocation);
		// Observers are never in ElectionOfficial. Their newest posting scopes the report — and
		// stamps the election id without which VESR stores it unscoped.
		if (isObserver) return contextFromObserverPosting(observer.postings[0]);
		const officials = states?._election?.get_ElectionOfficial?.data;
		const official = officials?.find((i) => i.userId === Number(getUserData().user?.UserId));
		// No state-name fallback is available yet. This used to read
		// `getProfile_Auth.stateName`, but that endpoint (User/GetUserByEmail) has never
		// returned a state name — and the reducer stores the response envelope, so the
		// access resolved to `undefined` twice over. `contextFromAssignment` already
		// degrades to the ordinal from the assignment code, which is what has always
		// rendered. The real name arrives with the agent-profile endpoint.
		return contextFromAssignment(official, null);
	}, [data.sitrepLocation, states?._election?.get_ElectionOfficial?.data, isObserver, observer.postings]);

	// Pinned "cancelled by INEC" tile — the agent's OWN polling unit only (never a child unit
	// reached through View Result), same gate as the home row.
	const ownOfficial = states?._election?.get_ElectionOfficial?.data?.find((i) => i.userId === Number(getUserData().user?.UserId));
	const cancellationGate =
		ownOfficial?.assignment?.resultType?.toLowerCase() === 'ec8a' && !ISOBSERVER() && puCancellationEnabled();
	const cancellation = useCancellationStatus(ownOfficial?.electionId, ownOfficial?.assignment?.code, cancellationGate);
	// Filed, on the server, and the server takes files → the tile and the prompt lead to evidence.
	const canAddEvidence = cancellation.snapshot.active && !cancellation.snapshot.localOnly && evidenceAllowed(cancellation.caps);
	const openCancellation = () => {
		if (!ownOfficial) return;
		const filed = cancellation.snapshot.active;
		openCancellationPanel(rsProps, {
			electionId: ownOfficial.electionId,
			pollingUnitCode: ownOfficial.assignment.code,
			pollingUnitName: ownOfficial.assignment.name,
			mode: filed && canAddEvidence ? 'add-evidence' : 'report',
		});
	};

	// One hub-scoped SignalR connection (state room) for the panel's lifetime.
	const { liveIncidents, liveStatus } = useIncidentLive(ctx.stateCode);

	const focusTab = (index: number) => {
		const next = (index + SEGMENTS.length) % SEGMENTS.length;
		tabRefs.current[next]?.focus();
		setSegment(SEGMENTS[next].key);
	};

	const onTabKeyDown = (e: KeyboardEvent, index: number) => {
		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				e.preventDefault();
				focusTab(index + 1);
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				e.preventDefault();
				focusTab(index - 1);
				break;
			case 'Home':
				e.preventDefault();
				focusTab(0);
				break;
			case 'End':
				e.preventDefault();
				focusTab(SEGMENTS.length - 1);
				break;
			default:
				break;
		}
	};

	const countOf = (key: HubSegment): number | null => {
		if (key === 'mine') return mineCount;
		if (key === 'nearby') return nearbyCount;
		return null;
	};

	const openAppIssues = () => {
		rsProps?.callSection({
			action: 'view',
			component: 'report-issues',
			title: 'Report Issues',
			max: true,
		});
	};

	return (
		<div className='reports-hub'>
			{cancellationGate ? (
				<button
					type='button'
					className={`rh-cancel-tile${cancellation.snapshot.active ? ' is-filed' : ''}`}
					onClick={openCancellation}
				>
					<span className='rh-cancel-tile__title'>
						{cancellation.snapshot.active
							? canAddEvidence
								? 'Cancellation reported · add evidence'
								: 'Cancellation reported · view report'
							: 'Polling unit cancelled by INEC?'}
					</span>
					<span className='rh-cancel-tile__hint'>
						{cancellation.snapshot.active
							? canAddEvidence
								? 'Photos, a voice note or video'
								: 'Status, withdraw and review'
							: 'Tell your party in three taps'}
					</span>
				</button>
			) : null}
			<div
				className='rh-tabs'
				role='tablist'
				aria-label='Reports views'
			>
				{SEGMENTS.map((seg, index) => {
					const isActive = segment === seg.key;
					const count = countOf(seg.key);
					return (
						<button
							key={seg.key}
							ref={(el) => (tabRefs.current[index] = el)}
							type='button'
							role='tab'
							id={`rh-tab-${seg.key}`}
							aria-selected={isActive}
							aria-controls={`rh-panel-${seg.key}`}
							tabIndex={isActive ? 0 : -1}
							className={`rh-tabs__tab ${isActive ? 'is-active' : ''}`}
							onClick={() => setSegment(seg.key)}
							onKeyDown={(e) => onTabKeyDown(e, index)}
						>
							<span className='rh-tabs__label-full'>{seg.label}</span>
							<span className='rh-tabs__label-short'>{seg.short}</span>
							{count != null && count > 0 ? <span className='rh-tabs__count'>{count}</span> : null}
							{seg.key === 'mine' && liveStatus === 'connected' ? (
								<span
									className='rh-tabs__live'
									title='Live updates connected'
									aria-label='live updates connected'
								/>
							) : null}
						</button>
					);
				})}
			</div>

			<div
				className='rh-panel'
				role='tabpanel'
				id={`rh-panel-${segment}`}
				aria-labelledby={`rh-tab-${segment}`}
			>
				{/* Stays mounted (hidden) so a half-completed report — or the
				    receipt — survives the agent peeking at another segment. Its
				    state is component-local, so the always-mounted render-loop
				    trap (shared useStateHook) doesn't apply here. */}
				<div className={segment === 'new' ? '' : 'd-none'}>
					<NewReport
						ctx={ctx}
						onTrackReport={() => setSegment('mine')}
						cancellationActive={cancellationGate && canAddEvidence}
						onAddToCancellation={openCancellation}
					/>
				</div>
				{segment === 'mine' ? (
					<MyReports
						liveIncidents={liveIncidents}
						liveStatus={liveStatus}
						onCount={setMineCount}
						onFileFirst={() => setSegment('new')}
					/>
				) : null}
				{segment === 'nearby' ? <Nearby onCount={setNearbyCount} /> : null}
			</div>

			<div className='rh-footer'>
				<button
					type='button'
					className='rh-footer__link'
					onClick={openAppIssues}
				>
					Having a problem with the app instead? Report an app issue →
				</button>
			</div>
		</div>
	);
};

export default ReportsHub;
